/**
 * LocationTypeahead — FE #256.
 *
 * Notion-style picker for the Browse Location layout.  Type-to-filter
 * the user's free-text locations (e.g. "garage shelf 2", "hall closet
 * — top shelf"), tap a row to scope the map + grid to that location.
 *
 * Why this exists: most of the backfilled inventory has NO GPS — the
 * in-app SectionInfoBanner already admits this.  So the Memory Map
 * alone can't be the location finder; users with non-GPS items need a
 * free-text path back to "everything in the garage."  Notion's
 * Recently-used + A-Z dropdown is the precedent the spec calls out
 * (`docs/founsi-ai/search-ux.md §3.2`).
 *
 * Differences from `TagTypeahead`:
 *   - Single-select (locations are 1:1 on items, not many:1 like tags),
 *     so no `AND` operator pill and no multi-chip row.
 *   - Suggestions come from a hybrid source: `selectAllLocations`
 *     reads the local `inventoryItemsEnhanced` cache + a memoized
 *     count-desc sort (zero round-trip).  When the user types and
 *     hits zero matches we currently DO NOT call the BE — the new
 *     value is staged as-is into `filters.location`, on the
 *     assumption that the user is creating a label that will get
 *     populated as items reach it.  v2 can call `GET
 *     /users/locations/top` and merge results.
 *   - On select we ALSO switch the layout pill back to Recent so the
 *     user lands on the filtered grid view (per the spec's
 *     "view-scoped filter with expand-to-global escape hatch"
 *     pattern).
 */

import React, { useMemo, useState } from 'react';
import {
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import {
  selectAllLocations,
  selectFilters,
  setFilters,
} from '../browseSlice';

const MAX_DROPDOWN_ROWS = 8;

interface LocationTypeaheadProps {
  /** Fires AFTER `filters.location` is set, before the dropdown
   *  collapses.  The Browse screen wires this to its local layout
   *  setter so the picker can land the user on Recent — the
   *  "view-scoped filter with expand-to-global escape hatch" pattern
   *  from Linear.  Layout is currently component-local state in
   *  BrowseScreen (not Redux), so the parent has to bridge it. */
  onAfterPick?: () => void;
}

export const LocationTypeahead: React.FC<LocationTypeaheadProps> = ({
  onAfterPick,
}) => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilters);
  const allLocations = useAppSelector(selectAllLocations);
  const active = filters.location ?? '';

  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = allLocations.filter(
      (l) => l.location.toLowerCase() !== active.toLowerCase(),
    );
    if (!q) return pool.slice(0, MAX_DROPDOWN_ROWS);
    return pool
      .filter((l) => l.location.toLowerCase().includes(q))
      .slice(0, MAX_DROPDOWN_ROWS);
  }, [allLocations, query, active]);

  const showCreate =
    query.trim().length > 0 &&
    !suggestions.some(
      (l) => l.location.toLowerCase() === query.trim().toLowerCase(),
    );

  const pickLocation = (loc: string): void => {
    const t = loc.trim();
    if (!t) return;
    dispatch(setFilters({ ...filters, location: t }));
    // Switch back to Recent so the user lands on the filtered grid.
    // Linear's "view-scoped filter with expand-to-global escape hatch"
    // pattern — the typeahead is a finder, not a destination.  Layout
    // is currently component-local state on BrowseScreen, so the
    // parent does the switch via the callback.
    onAfterPick?.();
    setQuery('');
    setFocused(false);
  };

  const clearActive = (): void => {
    dispatch(setFilters({ ...filters, location: undefined }));
  };

  return (
    <View style={styles.wrap} accessibilityLabel="Location typeahead">
      {/* Active chip if a location filter is set (rarely seen on this
          layout since we switch to Recent on pick, but visible when
          the user navigates back). */}
      {!!active && (
        <View style={styles.activeRow}>
          <View style={styles.activeChip}>
            <MaterialIcons
              name="place"
              size={14}
              color={fpColors.onPrimary}
              style={{ marginRight: 4 }}
            />
            <Text style={styles.activeChipLabel}>{active}</Text>
            <Pressable
              onPress={clearActive}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Clear location filter"
            >
              <MaterialIcons
                name="close"
                size={14}
                color={fpColors.onPrimary}
                style={{ marginLeft: 6 }}
              />
            </Pressable>
          </View>
        </View>
      )}

      <View style={styles.inputWrap}>
        <MaterialIcons
          name="place"
          size={18}
          color={fpColors.onSurfaceVariant}
          style={styles.inputIcon}
        />
        <TextInput
          value={query}
          onChangeText={setQuery}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setTimeout(() => setFocused(false), 120);
          }}
          placeholder="Jump to a location — e.g. garage shelf 2"
          placeholderTextColor={fpColors.onSurfaceVariant}
          style={styles.input}
          autoCorrect={false}
          autoCapitalize="none"
          onSubmitEditing={() => {
            if (suggestions.length > 0) pickLocation(suggestions[0].location);
            else if (showCreate) pickLocation(query.trim());
          }}
          accessibilityLabel="Location filter input"
        />
        {query.length > 0 && (
          <Pressable
            onPress={() => setQuery('')}
            accessibilityRole="button"
            accessibilityLabel="Clear location search"
            hitSlop={8}
          >
            <MaterialIcons
              name="close"
              size={18}
              color={fpColors.onSurfaceVariant}
            />
          </Pressable>
        )}
      </View>

      {focused && (suggestions.length > 0 || showCreate) && (
        <View style={styles.dropdown}>
          <FlatList
            data={suggestions}
            keyboardShouldPersistTaps="handled"
            keyExtractor={(item) => `loc-${item.location}`}
            renderItem={({ item }) => (
              <Pressable
                onPress={() => pickLocation(item.location)}
                style={({ pressed }) => [
                  styles.row,
                  pressed && styles.rowPressed,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`Filter by location ${item.location}`}
              >
                <Text style={styles.rowLabel} numberOfLines={1}>
                  {item.location}
                </Text>
                <Text style={styles.rowCount}>
                  {item.count === 1 ? '1 item' : `${item.count} items`}
                </Text>
              </Pressable>
            )}
            ListFooterComponent={
              showCreate ? (
                <Pressable
                  onPress={() => pickLocation(query.trim())}
                  style={({ pressed }) => [
                    styles.row,
                    styles.createRow,
                    pressed && styles.rowPressed,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={`Use location ${query.trim()}`}
                >
                  <MaterialIcons
                    name="add"
                    size={16}
                    color={fpColors.primary}
                    style={{ marginRight: 6 }}
                  />
                  <Text style={styles.createLabel} numberOfLines={1}>
                    {`Use "${query.trim()}" as a new location filter`}
                  </Text>
                </Pressable>
              ) : null
            }
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  activeRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  activeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: fpColors.primary,
  },
  activeChipLabel: {
    color: fpColors.onPrimary,
    fontSize: 12,
    fontWeight: '600',
  },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    paddingHorizontal: 10,
    paddingVertical: Platform.OS === 'ios' ? 8 : 4,
  },
  inputIcon: {
    marginRight: 6,
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
    padding: 0,
    margin: 0,
  },
  dropdown: {
    marginTop: 6,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
    maxHeight: 280,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: fpColors.outlineVariant,
  },
  rowPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  rowLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: fpColors.onSurface,
  },
  rowCount: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  createRow: {
    borderBottomWidth: 0,
  },
  createLabel: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: fpColors.primary,
  },
});

export default LocationTypeahead;
